/**
 * Route wrapper for "Navrhnout opravu" (CZNA-767) — proposing a correction to an
 * already-published record, i.e. creating an UPDATE_EXISTING proposal with a
 * `targetRecordId`.
 *
 * Reached from `RecordDetail`'s propose-change button:
 * `/czna-authorities/proposals/new?targetRecordId=<id>`. That link existed since
 * TASK-070 with no route to answer it (traced by the CZNA-767 research pass to
 * commit 75b51187) — until now `/new` fell into `Proposals.tsx`'s catch-all
 * `:id` route, which tried to load a proposal named "new" and 404ed.
 *
 * Deliberately feeds {@link ProposalForm}, not `RecordForm` directly the way
 * `RecordEdit` does: `ProposalForm` already owns the create POST, the validation
 * preflight and the needsReview banner for every other create path (CZNA-767 is
 * a proposal creation, not a direct edit), and its UPDATE_EXISTING plumbing
 * (008/040 stamping, the diff view, `isFoundingForm`'s sigla suppression) was
 * already built for the review/approve/publish side of this proposal type —
 * only creating one was missing.
 *
 * Two components for the same reason as `RecordEdit`: `useAllowedProposalActions`
 * needs `record.type`, and React forbids calling that after the record-loading
 * early return.
 */
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';
import useRecordDetail from '../hooks/useRecordDetail';
import useAllowedProposalActions from '../hooks/useAllowedProposalActions';
import useFormConfig from '../hooks/useFormConfig';
import ProposalForm from '../components/ProposalForm/ProposalForm';
import { matchTemplateShape } from '../components/ProposalForm/utils/templateShapeRouting';
import { recordDataFieldsToProposalData } from '../components/ProposalForm/utils/recordDataFieldsToProposalData';
import { ProposalGateLoading, ProposalGateMessage } from '../components/ProposalGate';
import TargetRecordStateBanner from '../components/TargetRecordState';
import { ProposalType } from '../types/api';
var DRAFTS_WORKSPACE = '/czna-authorities/authorities/my-drafts';
var BACK_LABEL = 'ui-czna-authorities.proposals.create.denied.back';
/** The form itself, mounted only once the target record is loaded and known to be amendable. */
var ProposalCreateFromRecordForm = function (_a) {
    var _b;
    var record = _a.record;
    var history = useHistory();
    // CZNA-1403 (blind review) — the version the form's values were seeded from, frozen at
    // mount: the form keeps its first values, while a refetch of the record (refetchOnMount,
    // an invalidation) would hand a newer `version` that approval would then wrongly accept.
    var seededVersion = React.useState(record.version)[0];
    // CZNA-1087 — amend the record in the form its SHAPE can satisfy, the same routing
    // `RecordEdit` does and for the same reason: this route seeds the form from the
    // target's own `dataFields`, so a předmětová kategorie opened on the skupina form
    // would be asked for a `190$9` it structurally cannot have (and offered no `190$k`
    // to correct). `ProposalForm` owns the config fetch, so this call is only for the
    // routing table it publishes — react-query serves the second reader from the same
    // cache entry, and for every shape that is already the default the id is `undefined`,
    // i.e. the call ProposalForm was going to make anyway.
    var defaultShapeConfig = useFormConfig(record.type).config;
    var shapeTemplateId = matchTemplateShape(defaultShapeConfig === null || defaultShapeConfig === void 0 ? void 0 : defaultShapeConfig.templateRouting, record.dataFields, defaultShapeConfig === null || defaultShapeConfig === void 0 ? void 0 : defaultShapeConfig.templateId);
    return (React.createElement(ProposalForm, { recordType: record.type, templateId: shapeTemplateId, proposalType: ProposalType.UPDATE_EXISTING, 
        // CZNA-767 — seed the form with the target's own current data, exactly the
        // converter `RecordEdit` already uses to feed the direct-edit form from the
        // same grouped `dataFields` shape.
        initialData: recordDataFieldsToProposalData((_b = record.dataFields) !== null && _b !== void 0 ? _b : []), targetRecordId: record.id, targetRecordVersion: seededVersion, targetRecordAuthorityId: record.authorityId, targetRecordExportId: record.exportId, 
        // CZNA-1525 — a record still in local or NK review, or returned, may be changed, but
        // the cataloguer is told the change waits for the record's approval. Not a gate.
        notice: (React.createElement(TargetRecordStateBanner, { record: {
                id: record.id,
                authorityId: record.authorityId,
                status: record.status,
                reviewStatus: record.reviewStatus,
                reviewComment: record.reviewComment,
            } })), onCancel: function () { return history.push("/czna-authorities/authorities/".concat(record.id)); }, returnListUrl: DRAFTS_WORKSPACE }));
};
var ProposalCreateFromRecord = function () {
    var _a, _b;
    var intl = useIntl();
    var history = useHistory();
    var location = useLocation();
    var targetRecordId = (_a = new URLSearchParams(location.search).get('targetRecordId')) !== null && _a !== void 0 ? _a : undefined;
    var _c = useRecordDetail(targetRecordId), record = _c.record, isRecordLoading = _c.isLoading, isRecordError = _c.isError, recordError = _c.error;
    var _d = useAllowedProposalActions(), isAuthorizationLoading = _d.isLoading, isAuthorizationError = _d.isError, canCreateProposalOfType = _d.canCreateProposalOfType;
    var handleClose = function () { return history.push(DRAFTS_WORKSPACE); };
    var renderMessage = function (testId, heading, body) { return (React.createElement(ProposalGateMessage, { testId: testId, heading: heading, body: body, backLabelId: BACK_LABEL, onClose: handleClose })); };
    // Missing param: reachable only by a malformed link — the button that builds this
    // URL always appends it. Fails the same way a genuinely missing record does.
    //
    // CZNA-767 (blind review) — a record-fetch error used to be reported as "not found"
    // unconditionally, which is wrong for anything that ISN'T a 404 (a network blip, a
    // 500): the record may well exist, and "not found" tells the cataloguer to give up
    // on the wrong grounds. `RecordDetail` already makes exactly this distinction for
    // the same underlying `useRecordDetail` error shape — mirrored here rather than
    // re-derived.
    var is404 = recordError && typeof recordError === 'object' && 'response' in
        recordError
        && ((_b = recordError.response) === null || _b === void 0 ? void 0 : _b.status) === 404;
    if (!targetRecordId || (isRecordError && is404)) {
        return renderMessage('proposal-create-from-record-not-found', React.createElement(FormattedMessage, { id: "ui-czna-authorities.authorities.detail.notFound" }), React.createElement(FormattedMessage, { id: "ui-czna-authorities.authorities.detail.notFoundDescription" }));
    }
    if (isRecordError) {
        return renderMessage('proposal-create-from-record-load-error', React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposals.create.unavailable.heading" }), React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposals.create.unavailable.message" }));
    }
    // Both queries arrive asynchronously and independently; rendering a denial
    // during either window would flash "nemáte oprávnění" or an empty pane at a
    // legitimate cataloguer, the same trap CZNA-591 fixed on the create-new route.
    if (isRecordLoading || isAuthorizationLoading || !record) {
        return React.createElement(ProposalGateLoading, null);
    }
    if (isAuthorizationError) {
        return renderMessage('proposal-create-from-record-unavailable', React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposals.create.unavailable.heading" }), React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposals.create.unavailable.message" }));
    }
    // CZNA-767 — same rule the backend enforces (`ProposalServiceImpl#createProposal`
    // refuses a non-ACTIVE target with 409): a merged or deleted record has no
    // "current state" left to amend. Checked here so the cataloguer learns that
    // before filling out a form the save would refuse, not after.
    if (record.status !== undefined && record.status !== 'ACTIVE') {
        return renderMessage('proposal-create-from-record-not-active', React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposals.createFromRecord.notActive.heading" }), React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposals.createFromRecord.notActive.message" }));
    }
    if (!canCreateProposalOfType(record.type)) {
        return renderMessage('proposal-create-from-record-denied', React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposals.create.denied.type.heading" }), React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposals.create.denied.type.message", values: {
                type: intl.formatMessage({ id: "ui-czna-authorities.recordType.".concat(record.type) }),
            } }));
    }
    return React.createElement(ProposalCreateFromRecordForm, { record: record });
};
export default ProposalCreateFromRecord;
